import React, { useEffect } from "react";
import {
  ArrowRight,
  Check,
  Code2,
  Gauge,
  Globe,
  Layers3,
  MonitorSmartphone,
  Rocket,
  Server,
  ShieldCheck,
  ShoppingCart,
  Smartphone,
  Sparkles,
  Wrench,
} from "lucide-react";

import Header from "../components/Header";
import Footer from "../components/Footer";
import novafyThread from "../assets/images/novafy-thread.jpeg";

const orderLink = "https://t.me/rankifydev";

const services = [
  {
    title: "Business Websites",
    description:
      "Modern, responsive and conversion-focused websites built around your business goals.",
    icon: Globe,
  },
  {
    title: "E-commerce Development",
    description:
      "Fast online stores with smooth product discovery, secure checkout and scalable management.",
    icon: ShoppingCart,
  },
  {
    title: "Web Applications",
    description:
      "Custom dashboards, portals and web applications designed for real operational requirements.",
    icon: MonitorSmartphone,
  },
  {
    title: "Mobile Applications",
    description:
      "Android, iOS and cross-platform applications with clean UI and reliable performance.",
    icon: Smartphone,
  },
  {
    title: "Custom Business Systems",
    description:
      "Admin panels, automation tools, booking systems, CRMs and tailored digital workflows.",
    icon: Layers3,
  },
  {
    title: "Fixes & Optimisation",
    description:
      "Performance improvement, bug fixing, security cleanup, migration and website maintenance.",
    icon: Wrench,
  },
];

const benefits = [
  {
    title: "Performance First",
    description:
      "Fast-loading experiences built to reduce drop-offs and improve engagement.",
    icon: Gauge,
  },
  {
    title: "Scalable Development",
    description:
      "Clean architecture that can support new features, users and business growth.",
    icon: Rocket,
  },
  {
    title: "Secure & Reliable",
    description:
      "Secure development practices, stable integrations and dependable implementation.",
    icon: ShieldCheck,
  },
  {
    title: "SEO-Friendly Structure",
    description:
      "Development planned around technical SEO, mobile usability and search visibility.",
    icon: Sparkles,
  },
];

const technologies = [
  "React",
  "Next.js",
  "Node.js",
  "Laravel",
  "Flutter",
  "React Native",
  "MongoDB",
  "MySQL",
  "Shopify",
  "WordPress",
];

const process = [
  {
    number: "01",
    title: "Share Your Requirements",
    description:
      "Tell us what you want to build, your goals, references and required functionality.",
  },
  {
    number: "02",
    title: "Scope & Project Plan",
    description:
      "We review the requirements and provide the recommended approach, timeline and cost.",
  },
  {
    number: "03",
    title: "Design & Development",
    description:
      "The product is developed with regular updates, testing and feedback checkpoints.",
  },
  {
    number: "04",
    title: "Launch & Support",
    description:
      "After final approval, we launch the project and provide post-delivery assistance.",
  },
];

const faqItems = [
  {
    question: "What type of development projects do you accept?",
    answer:
      "We work on websites, e-commerce stores, web applications, mobile apps, admin dashboards, custom systems, API integrations and existing project improvements.",
  },
  {
    question: "Can you redesign or fix an existing website?",
    answer:
      "Yes. We can improve the design, speed, responsiveness, functionality, security and conversion flow of an existing website.",
  },
  {
    question: "Do you work with agencies and resellers?",
    answer:
      "Yes. NOVAFY is suitable for agencies, resellers and businesses requiring white-label or ongoing development support.",
  },
  {
    question: "Will I receive project updates?",
    answer:
      "Yes. We provide clear progress updates and review points throughout the development process.",
  },
  {
    question: "How do I get an exact price?",
    answer:
      "Send your requirements, reference links and required features. We will review them and provide a custom quotation.",
  },
];

const OrderButton = ({ className = "" }) => (
  <a
    href={orderLink}
    target="_blank"
    rel="noopener noreferrer"
    className={`group inline-flex items-center justify-center gap-2 rounded-full bg-[hsl(var(--brand-amber))] px-6 py-3.5 text-sm font-bold text-[hsl(var(--brand-navy))] shadow-lg transition hover:-translate-y-0.5 hover:shadow-xl ${className}`}
  >
    Order Now
    <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
  </a>
);

const NovafyPage = () => {
  useEffect(() => {
    document.title =
      "NOVAFY | Website, App & Custom Development Services | RankifyDev";
  }, []);

  return (
    <div className="App">
      <Header />

      <main className="pt-24">
        {/* Hero */}
        <section className="relative overflow-hidden pb-20 pt-16 sm:pt-20">
          <div className="absolute inset-0 brand-gradient-soft" aria-hidden />
          <div className="absolute -top-24 right-10 h-72 w-72 rounded-full brand-gradient blur-3xl opacity-30 blob-drift" />
          <div className="relative mx-auto max-w-7xl items-center px-4 sm:px-6 lg:px-8">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full border border-[hsl(var(--brand-violet)/.15)] bg-white/80 px-3.5 py-1 text-xs font-semibold text-brand-deep backdrop-blur">
                <Sparkles className="h-3.5 w-3.5" />
                RankifyDev Presents
              </div>

              <h2 className="mt-5 max-w-4xl text-4xl font-bold leading-tight text-[hsl(var(--brand-navy))] sm:text-4xl lg:text-5xl">
                Built for Growth{" "}
                <span className="text-gradient-brand">
                  NOVAFY
                </span>
              </h2>

              <p className="mt-6 max-w-2xl text-base leading-7 text-slate-600 sm:text-lg">
                Websites, mobile applications and custom business systems
                developed with clean code, modern UI/UX and business-focused
                functionality.
              </p>

              <div className="mt-8 flex flex-wrap items-center gap-3">
                <OrderButton />

                <a
                  href="#novafy-thread"
                  className="inline-flex items-center justify-center gap-2 rounded-full border border-black/10 bg-white px-6 py-3.5 text-sm font-semibold text-[hsl(var(--brand-navy))] transition hover:border-brand/40 hover:text-brand"
                >
                  View Complete Thread
                  <ArrowRight className="h-4 w-4" />
                </a>
              </div>

              <div className="mt-8 flex flex-wrap gap-x-6 gap-y-3 text-sm text-slate-600">
                {[
                  "Clean Development",
                  "Responsive Design",
                  "Scalable Systems",
                  "Transparent Process",
                ].map((item) => (
                  <div key={item} className="flex items-center gap-2">
                    <span className="grid h-5 w-5 place-items-center rounded-full bg-[hsl(var(--brand-violet)/.12)]">
                      <Check className="h-3 w-3 text-brand" />
                    </span>
                    {item}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
        {/* Thread */}
        <section id="novafy-thread" className="py-20">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="mx-auto max-w-3xl text-center">
              <div className="inline-flex rounded-full bg-lilac px-3 py-1 text-xs font-semibold text-brand-deep">
                Complete Service Thread
              </div>

              <h2 className="mt-4 text-3xl font-bold text-[hsl(var(--brand-navy))] sm:text-4xl lg:text-5xl">
                Explore the complete NOVAFY development service.
              </h2>

              <p className="mt-4 text-slate-600">
                View our complete service details, development capabilities,
                packages, technologies and project process.
              </p>
            </div>

            <div className="mt-10 overflow-hidden rounded-[2rem] border border-black/5 bg-white p-2 card-shadow sm:p-4">
              <a href="https://t.me/rankifydev" target="_blank"><img
                src={novafyThread}
                alt="NOVAFY development service thread by RankifyDev"
                loading="lazy"
                className="h-auto w-full rounded-2xl"
              /></a>
            </div>
          </div>
        </section>
        {/* Services */}
        <section className="py-10">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl">
              <div className="inline-flex rounded-full bg-lilac px-3 py-1 text-xs font-semibold text-brand-deep">
                What We Build
              </div>

              <h2 className="mt-4 text-3xl font-bold text-[hsl(var(--brand-navy))] sm:text-4xl lg:text-5xl">
                Development solutions designed around your business.
              </h2>

              <p className="mt-4 text-slate-600">
                From marketing websites to custom platforms, NOVAFY focuses on
                useful, reliable and growth-ready digital products.
              </p>
            </div>

            <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {services.map(({ title, description, icon: Icon }) => (
                <article
                  key={title}
                  className="group rounded-3xl border border-black/5 bg-white p-6 card-shadow transition duration-300 hover:-translate-y-1 hover:card-shadow-hover"
                >
                  <div className="grid h-12 w-12 place-items-center rounded-2xl brand-gradient">
                    <Icon className="h-6 w-6 text-white" />
                  </div>

                  <h3 className="mt-5 text-xl font-bold text-[hsl(var(--brand-navy))]">
                    {title}
                  </h3>

                  <p className="mt-3 text-sm leading-6 text-slate-600">
                    {description}
                  </p>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* Order CTA */}
        <section className="px-4 py-10 sm:px-6 lg:px-8">
          <div className="relative mx-auto max-w-7xl overflow-hidden rounded-[2rem] bg-[hsl(var(--brand-navy))] px-6 py-10 text-white sm:px-10 lg:flex lg:items-center lg:justify-between">
            <div className="absolute -right-16 -top-20 h-64 w-64 rounded-full brand-gradient opacity-40 blur-3xl" />

            <div className="relative max-w-3xl">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-white/60">
                Start your project
              </p>

              <h2 className="mt-3 text-3xl font-bold sm:text-4xl">
                Need a website, application or custom development solution?
              </h2>

              <p className="mt-3 text-white/70">
                Share your requirements and receive a custom project plan.
              </p>
            </div>

            <div className="relative mt-6 lg:mt-0">
              <OrderButton className="min-w-44" />
            </div>
          </div>
        </section>

        {/* Benefits */}
        <section className="bg-lilac/50 py-10">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="grid gap-10 lg:grid-cols-[.8fr_1.2fr] lg:items-center">
              <div>
                <div className="inline-flex rounded-full bg-white px-3 py-1 text-xs font-semibold text-brand-deep">
                  Why NOVAFY
                </div>

                <h2 className="mt-4 text-3xl font-bold text-[hsl(var(--brand-navy))] sm:text-4xl">
                  Built for performance, usability and long-term growth.
                </h2>

                <p className="mt-4 leading-7 text-slate-600">
                  We do not only focus on how a product looks. Every project is
                  planned around speed, scalability, user experience and
                  business outcomes.
                </p>

                <OrderButton className="mt-7" />
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                {benefits.map(({ title, description, icon: Icon }) => (
                  <div
                    key={title}
                    className="rounded-3xl border border-black/5 bg-white p-6 card-shadow"
                  >
                    <Icon className="h-7 w-7 text-brand" />

                    <h3 className="mt-4 text-lg font-bold text-[hsl(var(--brand-navy))]">
                      {title}
                    </h3>

                    <p className="mt-2 text-sm leading-6 text-slate-600">
                      {description}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Technology */}
        <section className="py-20">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <div className="inline-flex rounded-full bg-lilac px-3 py-1 text-xs font-semibold text-brand-deep">
                Technology Stack
              </div>

              <h2 className="mt-4 text-3xl font-bold text-[hsl(var(--brand-navy))] sm:text-4xl">
                Technologies selected around project requirements.
              </h2>
            </div>

            <div className="mx-auto mt-10 flex max-w-7xl flex-wrap justify-center gap-3">
              {technologies.map((technology) => (
                <div
                  key={technology}
                  className="inline-flex items-center gap-2 rounded-full border border-black/10 bg-white px-5 py-3 text-sm font-semibold text-[hsl(var(--brand-navy))] card-shadow"
                >
                  <Code2 className="h-4 w-4 text-brand" />
                  {technology}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Process */}
        <section className="brand-gradient-soft py-20">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl">
              <div className="inline-flex rounded-full bg-white/80 px-3 py-1 text-xs font-semibold text-brand-deep">
                Simple Process
              </div>

              <h2 className="mt-4 text-3xl font-bold text-[hsl(var(--brand-navy))] sm:text-4xl">
                From requirement to launch.
              </h2>
            </div>

            <div className="mt-10 grid gap-5 md:grid-cols-2 lg:grid-cols-4">
              {process.map(({ number, title, description }) => (
                <div
                  key={number}
                  className="rounded-3xl border border-white/80 bg-white/80 p-6 backdrop-blur card-shadow"
                >
                  <div className="text-sm font-bold text-brand">{number}</div>

                  <h3 className="mt-4 text-lg font-bold text-[hsl(var(--brand-navy))]">
                    {title}
                  </h3>

                  <p className="mt-3 text-sm leading-6 text-slate-600">
                    {description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-20">
          <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <div className="inline-flex rounded-full bg-lilac px-3 py-1 text-xs font-semibold text-brand-deep">
                Common Questions
              </div>

              <h2 className="mt-4 text-3xl font-bold text-[hsl(var(--brand-navy))] sm:text-4xl">
                NOVAFY FAQs
              </h2>
            </div>

            <div className="mt-10 space-y-4">
              {faqItems.map(({ question, answer }) => (
                <details
                  key={question}
                  className="group rounded-2xl border border-black/5 bg-white p-5 card-shadow"
                >
                  <summary className="flex cursor-pointer list-none items-center justify-between gap-4 font-bold text-[hsl(var(--brand-navy))]">
                    {question}

                    <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-lilac text-brand transition group-open:rotate-45">
                      +
                    </span>
                  </summary>

                  <p className="mt-4 max-w-3xl text-sm leading-6 text-slate-600">
                    {answer}
                  </p>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="px-4 pb-20 sm:px-6 lg:px-8">
          <div className="relative mx-auto max-w-7xl overflow-hidden rounded-[2.5rem] brand-gradient px-6 py-16 text-center text-white sm:px-10">
            <div className="absolute inset-0 noise-overlay" />

            <div className="relative mx-auto max-w-3xl">
              <p className="text-xs font-bold uppercase tracking-[0.22em] text-white/70">
                NOVAFY by RankifyDev
              </p>

              <h2 className="mt-4 text-4xl font-bold sm:text-5xl">
                Ready to build your next digital product?
              </h2>

              <p className="mx-auto mt-5 max-w-2xl leading-7 text-white/80">
                Share your idea, required features and reference websites. We
                will help turn it into a fast, scalable and business-ready
                product.
              </p>

              <OrderButton className="mt-8 min-w-52" />

              <div className="mt-5 text-sm text-white/75">
                Telegram: @rankifydev · Email: rankifydev22@gmail.com
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default NovafyPage;